import { useState, useEffect } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { selectLanguage } from '@utils'
import { BottomSheet } from '@/components/ui'
import { useSessionStore } from '@/stores'

interface LanguageOption {
  code: string
  label: string
}

const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsche' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Pусский' },
  { code: 'zh-CN', label: '中文' },
  { code: 'ko', label: '한국어' },
  { code: 'ja', label: '日本語' },
  { code: 'hi', label: 'हिंदी' },
]

export const Language = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const currentLanguage = useSessionStore(s => s.currentLanguage) || 'en'
  const setLanguage = useSessionStore(s => s.setLanguage)
  const campaign = useSessionStore(s => s.campaign) as any

  const getFlagImage = (code: string) => {
    try {
      return new URL(`../assets/images/languages/${code}.png`, import.meta.url).href
    } catch {
      return ''
    }
  }

  useEffect(() => {
    const cookies = document.cookie.split(';')
    const googleTransCookie = cookies.find(cookie => cookie.trim().startsWith('googtrans='))
    if (googleTransCookie) {
      const value = googleTransCookie.split('=')[1]
      if (value?.startsWith('/')) {
        const languages = value.slice(1).split('/')
        if (languages.length === 2) {
          setLanguage(languages[1])
          return
        }
      }
    }
  }, [setLanguage])

  const handleLanguageSelect = (languageCode: string) => {
    setLanguage(languageCode)
    selectLanguage(languageCode)
    setIsMobileOpen(false)
  }

  const getShortCode = () => currentLanguage.slice(0, 2).toUpperCase()

  if (campaign?.header_content) {
    return null
  }

  return (
    <div className="language">
      <div className="hidden sm:inline-block">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="language-button footer_btn_color flex items-center gap-1 no-transform"
              style={{ color: 'var(--text-color)' }}>
              <span className="selected-language">
                <img src={getFlagImage(currentLanguage)} alt={currentLanguage} width="20" height="20" />
              </span>
              <span className="hidden md:inline-block">Select</span>
              Language
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="language-dropdown-content" sideOffset={8} align="start">
              {LANGUAGES.map(language => (
                <DropdownMenu.Item
                  key={language.code}
                  className="language-dropdown-option"
                  onSelect={() => handleLanguageSelect(language.code)}>
                  <img src={getFlagImage(language.code)} alt={language.label} width="20" height="20" />
                  <span className="notranslate">{language.label}</span>
                  {currentLanguage === language.code && <Check className="lang-check w-4 h-4" />}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <div className="sm:hidden">
        <button
          type="button"
          className="language-button footer_btn_color no-transform"
          onClick={() => setIsMobileOpen(true)}>
          <span className="selected-language">
            <img src={getFlagImage(currentLanguage)} alt={currentLanguage} width="24" height="24" />
          </span>
          <span className="notranslate language-label">{getShortCode()}</span>
          <ChevronDown className="w-3 h-3 ml-1 inline-block" />
        </button>
      </div>

      <BottomSheet isOpen={isMobileOpen} onClose={() => setIsMobileOpen(false)} title="Select Language">
        <div className="mob-languages">
          {LANGUAGES.map(language => (
            <button
              key={language.code}
              type="button"
              className="language-option"
              onClick={() => handleLanguageSelect(language.code)}>
              <div className={currentLanguage === language.code ? 'mob-lang-active' : ''}>
                <img
                  src={getFlagImage(language.code)}
                  alt={language.label}
                  className="mob-lang-img"
                  width="24"
                  height="24"
                />
                <span className="notranslate">{language.label}</span>
                {currentLanguage === language.code && <Check className="lang-check w-4 h-4" />}
              </div>
            </button>
          ))}
        </div>
      </BottomSheet>
    </div>
  )
}

export default Language
